import { Analytics } from '@cobliteam/analytics';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import type { FeedItem } from '@knocklabs/client';
import { KnockFeedProvider, useKnockFeed } from '@knocklabs/react';
import { useEffect, useMemo, useRef } from 'react';
import useRiskRecognition, { buildRiskRecognitionEventProps, isHighlightRenderable } from '../../store/useRiskRecognition';
import { getFeedButtonLabelsFromItem, getHighlightFromFeedItem } from '../RiskRecognitionFloatingCard/feedHighlight';
import RiskRecognitionCardContent from '../RiskRecognitionFloatingCard/RiskRecognitionCardContent';

// Id fixo: reusar o mesmo toast faz cada novo evento substituir o anterior — só o último fica na tela.
const RISK_RECOGNITION_TOAST_ID = 'risk-recognition-toast';

// Notifica novos eventos de risco em QUALQUER página (diferente do card flutuante, que só renderiza em
// /monitoring). Subscreve apenas os pushes ao vivo do socket — não o fetch da montagem — então não dispara
// toast para histórico ao trocar de página; só para eventos genuinamente novos. Só o último evento fica
// visível: cada toast novo substitui o anterior (id fixo).
const RiskRecognitionToastListener = () => {
  const { feedClient } = useKnockFeed();
  // Dedup por id: o socket pode reemitir o mesmo item em batches diferentes — não toasta de novo.
  const toastedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const handleRealtime = ({ items }: { items: FeedItem[] }) => {
      for (const item of items) {
        if (toastedIdsRef.current.has(item.id)) continue;
        const highlight = getHighlightFromFeedItem(item);
        if (!(isHighlightRenderable(highlight, null) && highlight?.metadata)) continue;

        toastedIdsRef.current.add(item.id);
        Analytics.track('AI - Risk Recognition - Displayed', buildRiskRecognitionEventProps(highlight, 'toast'));
        const { action: actionLabel, dismiss: dismissLabel } = getFeedButtonLabelsFromItem(item);

        // Mesmo card flutuante do /monitoring, renderizado como toast pelo sonner (top-right, `unstyled`
        // pra não duplicar a moldura). Id FIXO → cada novo evento SUBSTITUI o anterior, só o último fica
        // visível (não empilha). Sem auto-dismiss.
        Alert.custom(
          (toastId) => (
            <RiskRecognitionCardContent
              highlight={highlight}
              actionLabel={actionLabel}
              dismissLabel={dismissLabel}
              className='w-[352px] max-w-[calc(100vw-2rem)]'
              onDismiss={() => {
                Analytics.track('AI - Risk Recognition - Dismissed', buildRiskRecognitionEventProps(highlight, 'toast'));
                Alert.dismiss(toastId);
              }}
              onAction={() => {
                Analytics.track('AI - Risk Recognition - Action Clicked', buildRiskRecognitionEventProps(highlight, 'toast'));
                void feedClient.markAsInteracted(item);
                useRiskRecognition.getState().openDrawer(highlight);
                Alert.dismiss(toastId);
              }}
            />
          ),
          {
            id: RISK_RECOGNITION_TOAST_ID,
            duration: Number.POSITIVE_INFINITY,
            unstyled: true,
            position: 'top-right',
            // Zera a moldura do wrapper do sonner (borda/padding/bg/sombra) — só a do card aparece.
            className: '!border-0 !bg-transparent !p-0 !shadow-none mt-10',
          }
        );
      }
    };

    feedClient.on('items.received.realtime', handleRealtime);
    return () => feedClient.off('items.received.realtime', handleRealtime);
  }, [feedClient]);

  return null;
};

// Cidadão de layout próprio: abre seu próprio KnockFeedProvider e vive fora de /monitoring de propósito,
// para que o toast apareça em qualquer página. Gateado pelas mesmas flags do card de risk recognition.
const RiskRecognitionToast = () => {
  const { knockToastRiskRecognition, knockInApp } = useFeatureFlags();
  const { getSession } = useUser();
  const session = getSession();
  const userId = session?.user?.id;
  const fleetId = session?.fleet?.id;
  const { knockAPIKey, knockFeedChannelId } = Environment.getInstance().getEnvironment();

  const defaultFeedOptions = useMemo(
    () => ({
      archived: 'exclude' as const,
      mode: 'rich' as const,
      // Sem filtro `source` no servidor: a API do Knock só aceita um valor, e há vários workflows de
      // risco. Buscamos mais itens e filtramos client-side por RISK_RECOGNITION_WORKFLOW_KEYS + shape.
      page_size: 10,
      status: 'all' as const,
      ...(fleetId ? { tenant: String(fleetId) } : {}),
    }),
    [fleetId]
  );

  if (!(knockToastRiskRecognition && knockInApp && knockAPIKey && knockFeedChannelId && userId)) return null;

  return (
    <KnockFeedProvider feedId={knockFeedChannelId} defaultFeedOptions={defaultFeedOptions}>
      <RiskRecognitionToastListener />
    </KnockFeedProvider>
  );
};

export default RiskRecognitionToast;
